// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Configuration from './configuration';

export function isSelectionContextModel(source: any): source is ISelectionContext {
    // Method Implementation: simple
    return source.modelName === 'SelectionContext';
}

export function isSelectionInitializationParamsModel(source: any): source is ISelectionInitializationParams {
    // Method Implementation: simple
    return source.modelName === 'SelectionInitializationParams';
}

export interface SelectionContextZodSchemaShape {
    entityKey?: string;

    layoutName?: string;

    validSizesGridName?: string;
}

export interface SelectionInitializationParamsZodSchemaShape {
    application?: string;

    modelGroup?: string;

    defaults?: Configuration.EntityStateQuestionZodSchemaShape[];
}

// Artifact Implementation: artifacts/zodSchemaType
export const SelectionContextZodSchema: Zod.ZodSchema<SelectionContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional(),
    layoutName: Zod.string().optional(),
    validSizesGridName: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SelectionInitializationParamsZodSchema: Zod.ZodSchema<SelectionInitializationParamsZodSchemaShape> = Zod.lazy(() => Zod.object({
    application: Zod.string().optional(),
    modelGroup: Zod.string().optional(),
    defaults: Zod.array(Configuration.EntityStateQuestionZodSchema).optional()
}).nonstrict());

export interface ISelectionContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly layoutName: string;

    readonly validSizesGridName: string;

    readonly isEntityKeyDefined: boolean;

    readonly isLayoutNameDefined: boolean;

    readonly isValidSizesGridNameDefined: boolean;
}

export interface ISelectionInitializationParams extends Wellspring.IModel {
    readonly modelName: string;

    readonly application: string;

    readonly modelGroup: string;

    readonly defaults: Configuration.EntityStateQuestion[];

    readonly isApplicationDefined: boolean;

    readonly isModelGroupDefined: boolean;

    readonly isDefaultsDefined: boolean;
}

export class SelectionContext implements ISelectionContext {
    public static readonly modelName: string = 'SelectionContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey', 'layoutName', 'validSizesGridName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SelectionContext.modelName;
    }

    private _entityKey: string;

    private _layoutName: string;

    private _validSizesGridName: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get layoutName(): string {
        // Method Implementation: simple
        return this._layoutName;
    }

    public get validSizesGridName(): string {
        // Method Implementation: simple
        return this._validSizesGridName;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set layoutName(value: string) {
        // Method Implementation: simple
        this._layoutName = value;
        this._isLayoutNameDefined = true;
    }

    public set validSizesGridName(value: string) {
        // Method Implementation: simple
        this._validSizesGridName = value;
        this._isValidSizesGridNameDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    private _isLayoutNameDefined: boolean;

    private _isValidSizesGridNameDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isLayoutNameDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutNameDefined;
    }

    public get isValidSizesGridNameDefined(): boolean {
        // Method Implementation: simple
        return this._isValidSizesGridNameDefined;
    }

    public constructor();
    public constructor(source: ISelectionContext);
    public constructor(source: Partial<Pick<SelectionContext, 'entityKey' | 'layoutName' | 'validSizesGridName'>>);
    public constructor(source?: ISelectionContext | Partial<Pick<SelectionContext, 'entityKey' | 'layoutName' | 'validSizesGridName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectionContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isLayoutNameDefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.isValidSizesGridNameDefined) {
                    this._validSizesGridName = source.validSizesGridName;
                    this._isValidSizesGridNameDefined = true;
                }
                else {
                    this._validSizesGridName = '';
                    this._isValidSizesGridNameDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.layoutName !== undefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
                
                if (source.validSizesGridName !== undefined) {
                    this._validSizesGridName = source.validSizesGridName;
                    this._isValidSizesGridNameDefined = true;
                }
                else {
                    this._validSizesGridName = '';
                    this._isValidSizesGridNameDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._layoutName = '';
            this._isLayoutNameDefined = false;
        
            this._validSizesGridName = '';
            this._isValidSizesGridNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SelectionContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isLayoutNameDefined && this.layoutName !== undefined)
            definedProperties.push('layoutName');
        if (this.isValidSizesGridNameDefined && this.validSizesGridName !== undefined)
            definedProperties.push('validSizesGridName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            case 'layoutName':
                return this.layoutName;
            case 'validSizesGridName':
                return this.validSizesGridName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            if (this.layoutName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layoutName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'null'));
            }
        }
        
        if (this.isValidSizesGridNameDefined && this.validSizesGridName !== undefined) {
            if (this.validSizesGridName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validSizesGridName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.validSizesGridName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validSizesGridName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'layoutName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layoutName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layoutName' cannot be null`);
                    this.layoutName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layoutName'`);
                }
        
                return true;
            case 'validSizesGridName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.validSizesGridName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'validSizesGridName' cannot be null`);
                    this.validSizesGridName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validSizesGridName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layoutName
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layoutName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layoutName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validSizesGridName
        if (this.isValidSizesGridNameDefined && this.validSizesGridName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.validSizesGridName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.validSizesGridName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layoutName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layoutName = <any>null;
                    break;
                case 1:
                    this.layoutName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validSizesGridName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.validSizesGridName = <any>null;
                    break;
                case 1:
                    this.validSizesGridName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SelectionContext {
        // Method Implementation: simple
        const schema = SelectionContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectionContextZodSchemaShape): SelectionContext {
        // Method Implementation: parseZodSchema
        return new SelectionContext({
            entityKey: schema.entityKey,
            layoutName: schema.layoutName,
            validSizesGridName: schema.validSizesGridName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            layoutName: this.isLayoutNameDefined ? ((this.layoutName === null ? undefined : this.layoutName)) : undefined,
            validSizesGridName: this.isValidSizesGridNameDefined ? ((this.validSizesGridName === null ? undefined : this.validSizesGridName)) : undefined,
        };
    }

    public clone(): SelectionContext {
        // Method Implementation: clone
        return new SelectionContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            layoutName: this.isLayoutNameDefined ? this.layoutName : undefined,
            validSizesGridName: this.isValidSizesGridNameDefined ? this.validSizesGridName : undefined
        });
    }
}

export class SelectionInitializationParams implements ISelectionInitializationParams {
    public static readonly modelName: string = 'SelectionInitializationParams';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'application', 'modelGroup', 'defaults' ];

    public get modelName(): string {
        // Method Implementation: simple
        return SelectionInitializationParams.modelName;
    }

    private _application: string;

    private _modelGroup: string;

    private _defaults: Configuration.EntityStateQuestion[];

    public get application(): string {
        // Method Implementation: simple
        return this._application;
    }

    public get modelGroup(): string {
        // Method Implementation: simple
        return this._modelGroup;
    }

    public get defaults(): Configuration.EntityStateQuestion[] {
        // Method Implementation: simple
        return this._defaults;
    }

    public set application(value: string) {
        // Method Implementation: simple
        this._application = value;
        this._isApplicationDefined = true;
    }

    public set modelGroup(value: string) {
        // Method Implementation: simple
        this._modelGroup = value;
        this._isModelGroupDefined = true;
    }

    public set defaults(value: Configuration.EntityStateQuestion[]) {
        // Method Implementation: simple
        this._defaults = value;
        this._isDefaultsDefined = true;
    }

    private _isApplicationDefined: boolean;

    private _isModelGroupDefined: boolean;

    private _isDefaultsDefined: boolean;

    public get isApplicationDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicationDefined;
    }

    public get isModelGroupDefined(): boolean {
        // Method Implementation: simple
        return this._isModelGroupDefined;
    }

    public get isDefaultsDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultsDefined;
    }

    public constructor();
    public constructor(source: ISelectionInitializationParams);
    public constructor(source: Partial<Pick<SelectionInitializationParams, 'application' | 'modelGroup' | 'defaults'>>);
    public constructor(source?: ISelectionInitializationParams | Partial<Pick<SelectionInitializationParams, 'application' | 'modelGroup' | 'defaults'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectionInitializationParamsModel(source)) {
                if (source.isApplicationDefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.isModelGroupDefined) {
                    this._modelGroup = source.modelGroup;
                    this._isModelGroupDefined = true;
                }
                else {
                    this._modelGroup = '';
                    this._isModelGroupDefined = false;
                }
                
                if (source.isDefaultsDefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
            else {
                if (source.application !== undefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.modelGroup !== undefined) {
                    this._modelGroup = source.modelGroup;
                    this._isModelGroupDefined = true;
                }
                else {
                    this._modelGroup = '';
                    this._isModelGroupDefined = false;
                }
                
                if (source.defaults !== undefined) {
                    this._defaults = source.defaults;
                    this._isDefaultsDefined = true;
                }
                else {
                    this._defaults = [];
                    this._isDefaultsDefined = true;
                }
            }
        }
        else {
            this._application = '';
            this._isApplicationDefined = false;
        
            this._modelGroup = '';
            this._isModelGroupDefined = false;
        
            this._defaults = [];
            this._isDefaultsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return SelectionInitializationParams.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isApplicationDefined && this.application !== undefined)
            definedProperties.push('application');
        if (this.isModelGroupDefined && this.modelGroup !== undefined)
            definedProperties.push('modelGroup');
        if (this.isDefaultsDefined && this.defaults !== undefined)
            definedProperties.push('defaults');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'application':
                return this.application;
            case 'modelGroup':
                return this.modelGroup;
            case 'defaults':
                return this.defaults;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isApplicationDefined && this.application !== undefined) {
            if (this.application !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.application);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'null'));
            }
        }
        
        if (this.isModelGroupDefined && this.modelGroup !== undefined) {
            if (this.modelGroup !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('modelGroup', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.modelGroup);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('modelGroup', 'null'));
            }
        }
        
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            if (this.defaults !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.defaults.length));
                for (let i = 0; i < this.defaults.length; i++) {
                    const item = this.defaults[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaults', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'application':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.application = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'application' cannot be null`);
                    this.application = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'application'`);
                }
        
                return true;
            case 'modelGroup':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.modelGroup = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'modelGroup' cannot be null`);
                    this.modelGroup = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'modelGroup'`);
                }
        
                return true;
            case 'defaults':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.defaults = [];
                
                    let item: Configuration.EntityStateQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Configuration.EntityStateQuestion.modelName) {
                                item = new Configuration.EntityStateQuestion();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Configuration.EntityStateQuestion.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'defaults' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'defaults'`);
                        }
                
                        this.defaults.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'defaults' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaults'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // application
        if (this.isApplicationDefined && this.application !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.application === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.application);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // modelGroup
        if (this.isModelGroupDefined && this.modelGroup !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.modelGroup === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.modelGroup);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // defaults
        if (this.isDefaultsDefined && this.defaults !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.defaults === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.defaults.length);
            
                for (const item of this.defaults) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // application
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.application = <any>null;
                    break;
                case 1:
                    this.application = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // modelGroup
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.modelGroup = <any>null;
                    break;
                case 1:
                    this.modelGroup = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // defaults
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Configuration.EntityStateQuestion;
            
                    this.defaults = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Configuration.EntityStateQuestion();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.defaults.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): SelectionInitializationParams {
        // Method Implementation: simple
        const schema = SelectionInitializationParamsZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectionInitializationParamsZodSchemaShape): SelectionInitializationParams {
        // Method Implementation: parseZodSchema
        return new SelectionInitializationParams({
            application: schema.application,
            modelGroup: schema.modelGroup,
            defaults: schema.defaults?.map(i => Configuration.EntityStateQuestion.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            application: this.isApplicationDefined ? ((this.application === null ? undefined : this.application)) : undefined,
            modelGroup: this.isModelGroupDefined ? ((this.modelGroup === null ? undefined : this.modelGroup)) : undefined,
            defaults: this.isDefaultsDefined ? (this.defaults?.map(item => item)) : undefined,
        };
    }

    public clone(): SelectionInitializationParams {
        // Method Implementation: clone
        return new SelectionInitializationParams({
            application: this.isApplicationDefined ? this.application : undefined,
            modelGroup: this.isModelGroupDefined ? this.modelGroup : undefined,
            defaults: this.isDefaultsDefined ? this.defaults?.map(i => i.clone()) : undefined
        });
    }
}

export interface ISelectionService {
    init(context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;

    select(context: SelectionContext): Promise<void>;

    createProductEntity(context: SelectionContext, sizeId: string): Promise<string>;

    cleanup(context: SelectionContext): Promise<void>;
}

export class SelectionServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: SelectionContext, initializationParams: SelectionInitializationParams): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext), 
            // {"template":"model","className":"SelectionInitializationParams"}
            Zod.instanceof(SelectionInitializationParams)
        ]).check(args)) {
            const [ context, initializationParams ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("9500cad300b351d635fd2f462cfc95c77750c570899372a3510bcb1bc86ac603");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (initializationParams && initializationParams !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (initializationParams === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            initializationParams.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                initializationParams: JSON.parse(JSON.stringify(initializationParams))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        initializationParams: JSON.parse(JSON.stringify(initializationParams))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public select(context: SelectionContext): Promise<void>;
    public select(actionContext: Wellspring.ActionContext, context: SelectionContext): Promise<void>;
    public async select(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.select');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('select', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("d3147c6a306d9ec61b034b915bc85afc5e8912a600e378f5cb76e175e0ee0ffa");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/select" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createProductEntity(context: SelectionContext, sizeId: string): Promise<string>;
    public createProductEntity(actionContext: Wellspring.ActionContext, context: SelectionContext, sizeId: string): Promise<string>;
    public async createProductEntity(...args: unknown[]): Promise<string> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, sizeId ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.createProductEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createProductEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("c2b39389383c74ddf6599d03c8ec5f7fa11363a8c748120aa274a5d846c3697d");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (sizeId && sizeId !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (sizeId === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(sizeId);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<string>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].createProductEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                sizeId: JSON.parse(JSON.stringify(sizeId))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/createProductEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: string;
                    // Implementation: slipstream/read/string
                    switch (reader.readByteValue())
                    {
                        case 0:
                            result = <any>null;
                            break;
                        case 1:
                            result = reader.readStringValue();
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].createProductEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].createProductEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        sizeId: JSON.parse(JSON.stringify(sizeId))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public cleanup(context: SelectionContext): Promise<void>;
    public cleanup(actionContext: Wellspring.ActionContext, context: SelectionContext): Promise<void>;
    public async cleanup(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"SelectionContext"}
            Zod.instanceof(SelectionContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.cleanup');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('cleanup', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("d1df0ddc6b6d3d0f66dce5ef01289065c6f630378c92b382b123fd7bb6fd026b");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].cleanup`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/cleanup" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].cleanup`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'SelectionContext':
                return new SelectionContext();
            case 'SelectionInitializationParams':
                return new SelectionInitializationParams();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'SelectionContext':
                return new SelectionContext(source);
            case 'SelectionInitializationParams':
                return new SelectionInitializationParams(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'SelectionContext':
                return SelectionContext.parse(source);
            case 'SelectionInitializationParams':
                return SelectionInitializationParams.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
